/**
 * Calculator staleness helpers — pure functions, zero React coupling.
 *
 * A service is "stale" when the user-controlled inputs (and the slice of
 * measurement that fed those inputs) no longer match the snapshot that
 * produced `calculations[svc].input`.
 *
 * Staleness signals two user-facing affordances:
 *   1. "Recalc needed" pill in QuoteSummary next to the affected line.
 *   2. The Save Quote guard modal (Recalculate & Save / Save Anyway / Cancel).
 *
 * Snapshot architecture is preserved: results in `calculations[svc].result`
 * are still authoritative for QuoteSummary / Save / Print. Staleness only
 * gates UX affordances; it does NOT mutate `calculations`.
 */

/**
 * Which fields belong to each service's input signature. Anything not in
 * this list (e.g. `discount`, server-side rounding) is intentionally ignored
 * so we don't false-positive on legitimate display-only diffs.
 *
 * Source of truth for these keys: each panel's existing
 * `onServiceTotalChange(svc, { input: { … } })` call.
 */
const INPUT_KEYS = {
  roof:         ['sqFt', 'pitch', 'stage', 'stories', 'material'],
  siding:       ['sqFt', 'material', 'stories'],
  gutter:       ['linearFeet', 'stories'],
  driveway:     ['sqFt'],
  deck:         ['sqFt', 'material'],
  fence:        ['linearFeet', 'fenceHeight'],
  pest_control: ['sqFt', 'frequency'],
  lawn_care:    ['sqFt', 'frequency'],
};

/**
 * Canonicalise an inputs object → stable signature string.
 * Numbers are coerced to numbers (so "1500" and 1500 collide), undefined
 * is treated as null.
 */
export function getServiceInputsSignature(svc, inputs) {
  if (!inputs) return '';
  const keys = INPUT_KEYS[svc] || Object.keys(inputs);
  const norm = {};
  for (const k of keys) {
    const v = inputs[k];
    if (v === undefined || v === null || v === '') {
      norm[k] = null;
    } else if (typeof v === 'number') {
      norm[k] = v;
    } else if (!Number.isNaN(Number(v)) && /^-?\d*\.?\d+$/.test(String(v))) {
      // Looks numeric (e.g. "1500", "1500.5") → coerce so input field
      // changes that don't change the number aren't false-stale.
      norm[k] = Number(v);
    } else {
      norm[k] = String(v);
    }
  }
  return JSON.stringify(norm);
}

/**
 * Returns true if `calculations[svc]` is older than `currentInputs`.
 *
 * Returns false (NOT stale) when:
 *   - no calculation has ever been performed for this service (it's
 *     "uncalculated", not "stale" — show nothing).
 *   - the calculated input snapshot byte-matches current inputs.
 */
export function isCalculationStale(svc, calculations, currentInputs) {
  const calc = calculations?.[svc];
  if (!calc || !calc.result) return false;
  // A result of grandTotal=0 means the panel was opened but produced no
  // meaningful quote — don't badge as stale.
  const grand = Number(calc.result.grandTotal) || 0;
  if (grand <= 0) return false;
  // If we don't have a current-inputs mirror for this service, we cannot
  // know if it's stale. Treat as not-stale (the safe default).
  // This is the case for FlatRate-backed services (pest_control / lawn_care)
  // in v1 — they continue to work, they just don't participate in stale
  // detection or Recalculate All Stale.
  if (!currentInputs || Object.keys(currentInputs).length === 0) return false;
  const prevSig = getServiceInputsSignature(svc, calc.input || {});
  const nextSig = getServiceInputsSignature(svc, currentInputs || {});
  return prevSig !== nextSig;
}

/**
 * Returns an array of service ids whose calculations are stale relative
 * to `panelInputs` (parent-mirrored snapshot of each panel's current inputs).
 */
export function getStaleServices(calculations, panelInputs, selectedServices) {
  if (!calculations) return [];
  const out = [];
  const selected = new Set(selectedServices || []);
  for (const svc of Object.keys(calculations)) {
    if (svc === 'discount') continue;
    if (selected.size && !selected.has(svc)) continue;
    if (isCalculationStale(svc, calculations, panelInputs?.[svc])) out.push(svc);
  }
  return out;
}

/**
 * Human label per service for the Save guard modal listing.
 */
export const SERVICE_LABELS = {
  roof:         'Roof',
  siding:       'Siding',
  gutter:       'Gutters',
  driveway:     'Driveway',
  deck:         'Deck',
  fence:        'Fence',
  pest_control: 'Pest Control',
  lawn_care:    'Lawn Care',
};
